import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, CheckCircle, Loader2, Lock, User, ArrowRight } from 'lucide-react';
import API_URL from '../../config/api';

const AdminInviteRegister = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteCode = searchParams.get('code') || '';

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!inviteCode) return setError('Missing invitation code.');
    if (password.length < 6) return setError('Password must be at least 6 characters.');
    if (password !== confirmPwd) return setError('Passwords do not match.');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/internal-admins/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invitation_code: inviteCode, name, password })
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data);
        setTimeout(() => navigate('/infuse-admin'), 3000);
      } else {
        setError(data.detail || 'Registration failed.');
      }
    } catch {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  if (!inviteCode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 text-center">
          <div className="text-red-500 text-lg font-bold mb-2">Invalid invite link</div>
          <div className="text-sm text-gray-500">This page requires a valid invitation code from your Super Admin.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-purple-900 flex items-center justify-center p-4" data-testid="admin-invite-register-page">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center shadow-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-xs font-bold text-purple-600 uppercase tracking-widest">Infuse Admin Portal</div>
              <div className="text-lg font-bold text-gray-900">Complete Your Registration</div>
            </div>
          </div>

          {success ? (
            <div className="text-center py-6" data-testid="register-success">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="text-lg font-bold text-gray-900 mb-1">You're all set!</div>
              <div className="text-sm text-gray-500 mb-4">
                Welcome as a <span className="font-semibold text-purple-600">{success.admin_function?.replace('_', ' ')}</span>.
              </div>
              <div className="text-xs text-gray-400">Redirecting to admin portal…</div>
            </div>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="p-3 bg-purple-50 rounded-xl text-xs text-purple-800 flex gap-2">
                <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  You were invited by Infuse. Set your password below to access the admin portal with your assigned function permissions.
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" /> Full Name
                </label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} required data-testid="register-name"
                  className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-300 focus:border-purple-300"
                  placeholder="Your full name" />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" /> New Password
                </label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6}
                  data-testid="register-password"
                  className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-300 focus:border-purple-300"
                  placeholder="At least 6 characters" />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">Confirm Password</label>
                <input type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} required
                  data-testid="register-confirm"
                  className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-300 focus:border-purple-300"
                  placeholder="Re-enter password" />
              </div>

              {error && (
                <div className="p-2.5 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs" data-testid="register-error">
                  {error}
                </div>
              )}

              <button type="submit" disabled={loading} data-testid="register-submit"
                className="w-full py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-violet-600 transition shadow-lg flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRight className="w-4 h-4" /> Complete Registration</>}
              </button>
            </form>
          )}

          <div className="mt-6 pt-5 border-t border-gray-100 text-center">
            <div className="text-[10px] text-gray-400">
              Invitation code: <span className="font-mono">{inviteCode.substring(0, 8)}…</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminInviteRegister;
